import { useMemo, useState, useCallback } from 'react';
import type { CropTaxonomyTree, CropTaxonomyFamily, CropTaxonomyGenus, CropTaxonomySpecies } from '../../models/Crop';

interface TaxonomyPath {
  familyId: string;
  genusId: string;
  speciesId: string;
  cultivarId?: string;
}

export const findTaxonomyPath = (
  tree: CropTaxonomyTree | undefined,
  speciesId?: number | string | null,
  cultivarId?: number | string | null,
): TaxonomyPath | null => {
  if (!tree || speciesId === null || speciesId === undefined) return null;
  const speciesKey = String(speciesId);
  const cultivarKey = cultivarId === null || cultivarId === undefined ? null : String(cultivarId);

  for (const family of tree.families ?? []) {
    for (const genus of family.genera ?? []) {
      const species = genus.species?.find((item) => String(item.id) === speciesKey);
      if (species) {
        let cultivarPath: string | undefined;
        if (cultivarKey !== null) {
          const cultivar = species.cultivars?.find((item) => String(item.id) === cultivarKey);
          if (cultivar) cultivarPath = String(cultivar.id);
        }
        return {
          familyId: String(family.id),
          genusId: String(genus.id),
          speciesId: speciesKey,
          cultivarId: cultivarPath,
        };
      }
    }
  }

  return null;
};

/**
 * Hook для управления состоянием таксономии культуры
 */
export const useCropTaxonomy = (taxonomy: CropTaxonomyTree | undefined) => {
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  const [selectedGenusId, setSelectedGenusId] = useState<string | null>(null);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<string | null>(null);

  const families = useMemo(() => taxonomy?.families ?? [], [taxonomy]);

  const selectedFamily = useMemo<CropTaxonomyFamily | undefined>(() => {
    if (selectedFamilyId == null) return undefined;
    return families.find((family) => String(family.id) === selectedFamilyId);
  }, [families, selectedFamilyId]);

  const selectedGenus = useMemo<CropTaxonomyGenus | undefined>(() => {
    if (!selectedFamily) return undefined;
    if (selectedGenusId == null) return undefined;
    return selectedFamily.genera.find((genus) => String(genus.id) === selectedGenusId);
  }, [selectedFamily, selectedGenusId]);

  const selectedSpecies = useMemo<CropTaxonomySpecies | undefined>(() => {
    if (!selectedGenus) return undefined;
    if (selectedSpeciesId == null) return undefined;
    return selectedGenus.species.find((species) => String(species.id) === selectedSpeciesId);
  }, [selectedGenus, selectedSpeciesId]);

  const familyOptions = useMemo(
    () =>
      families.map((family) => ({
        value: String(family.id),
        label: family.name ? `${family.name} (${family.latinName})` : family.latinName,
      })),
    [families],
  );

  const genusOptions = useMemo(
    () =>
      (selectedFamily?.genera ?? []).map((genus) => ({
        value: String(genus.id),
        label: genus.name ? `${genus.name} (${genus.latinName})` : genus.latinName,
      })),
    [selectedFamily],
  );

  const speciesOptions = useMemo(
    () =>
      (selectedGenus?.species ?? []).map((species) => ({
        value: String(species.id),
        label: species.name ? `${species.name} (${species.latinName})` : species.latinName,
      })),
    [selectedGenus],
  );

  const cultivarOptions = useMemo(
    () =>
      (selectedSpecies?.cultivars ?? []).map((cultivar) => ({
        value: String(cultivar.id),
        label: cultivar.name ? `${cultivar.name} (${cultivar.latinName})` : cultivar.latinName,
      })),
    [selectedSpecies],
  );

  const reset = useCallback(() => {
    setSelectedFamilyId(null);
    setSelectedGenusId(null);
    setSelectedSpeciesId(null);
  }, []);

  const initializeFromPath = useCallback((path: TaxonomyPath) => {
    setSelectedFamilyId(path.familyId);
    setSelectedGenusId(path.genusId);
    setSelectedSpeciesId(path.speciesId);
  }, []);

  return {
    selectedFamilyId,
    selectedGenusId,
    selectedSpeciesId,
    selectedFamily,
    selectedGenus,
    selectedSpecies,
    familyOptions,
    genusOptions,
    speciesOptions,
    cultivarOptions,
    setSelectedFamilyId,
    setSelectedGenusId,
    setSelectedSpeciesId,
    reset,
    initializeFromPath,
  };
};
